import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useWindowSize } from '@uidotdev/usehooks'
import { getAgent } from 'utils/agent'
import { fetchApp, fetchAppId } from '../../utils/io'
import { getPreviewType } from '../../utils/previewType'
import { getAdaloAttributionSettings } from '../../utils/adaloAttribution'
import { MIN_SCREEN_WIDTH_SAFE_FOR_FRAME } from '../../utils/frame'
import { resolveDeepLink } from '../../utils/deepLinking'

import Loading from '../Shared/Loading'
import Renderer from '../Renderer'
import Frame from './Frame'
import { WEB, DEFAULT_SIZE } from './SizeMenu'
import AppDetails from './AppDetails'
import AdaloBadge from './AdaloBadge'
import NotFound from './NotFound'
import Stripe from './Stripe'
import DeepLinkPrompt from './DeepLinkPrompt'

import './Share.css'
import './WrappedSelect.css'
import { shouldUseNewReactNative } from '../../utils/libraries'

const Preview = () => {
  const { path, route } = useParams()
  const { width: windowWidth } = useWindowSize()
  const [app, setApp] = useState(null)
  const [error, setError] = useState(false)
  const [redirecting, setRedirecting] = useState(false)
  const [deepLink, setDeepLink] = useState(null)

  const getAppId = async () => {
    // in prod the web-preview-proxy adds the app id to the html
    const htmlAppId = document.querySelector('meta[property="adalo:appId"]')
      ?.content

    if (htmlAppId && htmlAppId !== 'APP_ID') {
      return htmlAppId
    }

    // locally or on staging: call backend to resolve app id with subdomain & path
    const host = window.location.host

    try {
      const appId = await fetchAppId(host, path)

      return appId
    } catch (error) {
      console.error('ERROR GETTING APP ID', error)

      // defaulting to appId in the path
      return path
    }
  }

  const redirectToBrowser = url => {
    if (!url) return

    setRedirecting(true)
    console.log('Redirecting to:', url)
    window.location.replace(url)
  }

  const handleRedirect = (alias, app) => {
    if (!alias || !app) {
      console.warn('Missing alias or app data for redirect')

      return
    }

    const agent = getAgent(window.navigator.userAgent)
    const link = resolveDeepLink({
      app,
      alias,
      agent,
      href: window.location.href,
    })

    if (!link) return

    // On iOS and Android, let the user choose. iOS keeps this page visible
    // while it shows its own "Open in app?" confirmation, so any automatic
    // fallback races that prompt and wins.
    if (link.nativeAppURL) {
      setDeepLink(link)

      return
    }

    redirectToBrowser(link.browserURL)
  }

  const handleFallback = () => {
    const { browserURL, isCustomFallback } = deepLink || {}

    // Custom Redirect is usually a store link. Keep the prompt behind it so a
    // user who installs the app can come back and tap Open in App.
    if (isCustomFallback) {
      window.location.assign(browserURL)

      return
    }

    setDeepLink(null)
    redirectToBrowser(browserURL)
  }

  const requestApp = async () => {
    try {
      const appId = await getAppId()

      const app = await fetchApp(appId)
      setApp(app)
      document.title = app.name
      if (route && path) {
        handleRedirect(route, app)
      }

      if (path && app) {
        handleRedirect(path, app)
      }
    } catch (error) {
      console.error('ERROR GETTING APP ID', error)
      setError(true)
    }
  }

  useEffect(() => {
    requestApp()
  }, [])

  // The browser may hand the redirect URL to another app (e.g. a store link)
  // and leave this tab where it was. Drop the spinner when the user returns.
  useEffect(() => {
    const handlePageShow = event => {
      if (event.persisted) setRedirecting(false)
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') setRedirecting(false)
    }

    window.addEventListener('pageshow', handlePageShow)
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      window.removeEventListener('pageshow', handlePageShow)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [])

  if (error) {
    return <NotFound />
  }

  if (app && deepLink) {
    return (
      <DeepLinkPrompt
        app={app}
        nativeAppURL={deepLink.nativeAppURL}
        isCustomFallback={deepLink.isCustomFallback}
        onFallback={handleFallback}
      />
    )
  }

  if (!app || redirecting) {
    return <Loading expanded />
  }

  const appId = app.id

  const { attributionComponent } = getAdaloAttributionSettings(
    app,
    windowWidth
  )

  let size = null
  const previewType = getPreviewType(app)

  if (previewType === 'web') {
    size = size || WEB
  }
  const useNewRN = shouldUseNewReactNative(app)
  if (size === WEB) {
    const offsetTop = attributionComponent === 'footer' ? 0 : 74

    return (
      <div>
        <div className="web-renderer-wrapper">
          <Renderer
            app={app}
            offsetTop={offsetTop}
            previewType={previewType}
            useNewRN={useNewRN}
          />
        </div>
        {attributionComponent === 'badge' && <AdaloBadge />}
      </div>
    )
  }

  if (windowWidth < MIN_SCREEN_WIDTH_SAFE_FOR_FRAME) {
    return (
      <>
        <Renderer
          analytics
          app={app}
          hideAppFooter={attributionComponent !== 'footer'}
          size={DEFAULT_SIZE}
          previewType={previewType}
          useNewRN={useNewRN}
        />
      </>
    )
  }

  size = size || DEFAULT_SIZE

  return (
    <React.Fragment>
      <Stripe branding={app.branding} className="top-stripe" />
      <div className="preview">
        <AppDetails app={app} />
        <Frame appId={appId} size={size} />
      </div>
      {attributionComponent === 'badge' && <AdaloBadge />}
    </React.Fragment>
  )
}

export default Preview
